import React, { Fragment, useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { Overlay, Popover } from "react-bootstrap";
import { useSelector } from "react-redux";

import Constants, { NAV_LINKS } from "../../js/constants";
import apiHandler from "../../js/apiHandler";
import { getSessionId } from "../../helpers/helpers";
import { withRouterHooks } from "../../helpers/withRouterHooks";
import AppModal from "./AppModal";
import HostAgentChat from "./HostAgentChat";
import "../../styles/userGuideController.less";
import { QuestionMarkIcon } from "./SvgIcons";

// Flatten every NAV_LINKS group once; we scan it whenever a guide needs to
// prepend a "navigate to the right page" step.
const ALL_NAV_LINKS = Object.values(NAV_LINKS || {}).reduce(
	(acc, group) => (Array.isArray(group) ? acc.concat(group) : acc),
	[],
);

const findMatchingNavLink = (urlPatterns) => {
	if (!Array.isArray(urlPatterns) || !urlPatterns.length) return null;
	for (const link of ALL_NAV_LINKS) {
		if (!link || typeof link.path !== "string") continue;
		for (const pattern of urlPatterns) {
			try {
				if (new RegExp(pattern).test(link.path)) return link;
			} catch (e) {
				// Skip malformed patterns silently.
			}
		}
	}
	return null;
};

const buildNavPrefixStep = (navLink) => ({
	order: 1,
	instruction: `Navigate to '${navLink.name}' from the side navigation to begin this walkthrough.`,
	selector: `.side-nav a[href="${navLink.path}"], .sidebar a[href="${navLink.path}"]`,
	action: "click",
	tooltip_position: "right",
	wait_for_navigation: true,
});

const GUIDE_CONTEXT = {
	PROPERTY: "property",
	RESERVATION: "reservation",
};

const GUIDE_SELECTION_LIMIT = 20;
const GUIDE_RESERVATION_SELECTION_LIMIT = 5;

// How long to keep polling for a step's target element before giving up. A
// guide can legitimately need a moment while a tab/page loads, but it must
// never poll forever: if the element isn't reachable from the current page
// (e.g. it lives on a different tab), we surface a graceful failure instead of
// spinning indefinitely.
const GUIDE_TARGET_WAIT_MS = 12000;

// Optional steps target a control that may legitimately not exist for this
// property/feature state (e.g. the "Modify Price" pill only renders with Smart
// Pricing). If such a step's target hasn't appeared after this short grace, we
// auto-advance instead of stalling, so one guide serves both UI variants.
const GUIDE_OPTIONAL_STEP_WAIT_MS = 3000;

const INITIAL_GUIDE_SELECTION = {
	show: false,
	type: null,
	guide: null,
	query: "",
	options: [],
	selected: null,
	isLoading: false,
	errorMessage: "",
};

const getGuideUrlPatterns = (guide) => {
	const context = guide?.start_context || {};
	return Array.isArray(context.url_patterns) ? context.url_patterns : [];
};

const hasDynamicRouteToken = (source, paramNames) => (
	source.includes("\\d") ||
	source.includes("[0-9") ||
	source.includes("[^/]+") ||
	paramNames.some((name) => source.includes(`:${name}`))
);

const normalizeGuidePattern = (pattern) => String(pattern || "").replace(/\\\//g, "/");

const isPropertyGuidePattern = (pattern) => {
	const source = normalizeGuidePattern(pattern);
	return (
		!source.includes("/properties/reservations") &&
		(source.includes("/properties/") ||
			source.includes("/collections/") ||
			source.includes("/calendar/")) &&
		hasDynamicRouteToken(source, ["property_id", "id"])
	);
};

const isReservationGuidePattern = (pattern) => {
	const source = normalizeGuidePattern(pattern);
	return (
		(
			source.includes("/reservations/") ||
			source.includes("/dashboard/reservations/") ||
			source.includes("/properties/reservations/")
		) &&
		hasDynamicRouteToken(source, ["reservation_id", "id"])
	);
};

const guideHasReservationIntent = (guide) => {
	const text = [
		guide?.title,
		guide?.description,
		...(Array.isArray(guide?.tags) ? guide.tags : []),
	].filter(Boolean).join(" ").toLowerCase();

	return /\breservation\b|\bguest\b|\binvoice\b|\bpayment\b/.test(text);
};

const getGuideRouteContext = (guide) => {
	const patterns = getGuideUrlPatterns(guide);
	if (patterns.some(isReservationGuidePattern)) return GUIDE_CONTEXT.RESERVATION;
	if (guideHasReservationIntent(guide) && patterns.some(isPropertyGuidePattern)) {
		return GUIDE_CONTEXT.RESERVATION;
	}
	if (patterns.some(isPropertyGuidePattern)) return GUIDE_CONTEXT.PROPERTY;
	return null;
};

const getPreferredGuidePattern = (guide, type) => {
	const patterns = getGuideUrlPatterns(guide);
	const matcher = type === GUIDE_CONTEXT.RESERVATION
		? isReservationGuidePattern
		: isPropertyGuidePattern;
	return patterns.find(matcher) || "";
};

const CURRENT_PROPERTY_ID_RE = /\/(?:properties|calendar|collections)\/(\d+)/;
const resolveCurrentPropertyId = (pathname) =>
	(CURRENT_PROPERTY_ID_RE.exec(pathname || "") || [])[1] || null;

const getPropertyOptionId = (property) => property?.property_id || property?.id || property?.listing_id || "";

const getReservationOptionId = (reservation) => reservation?.reservation_id || reservation?.id || "";

const getGuideSelectionOptionId = (type, option) => (
	type === GUIDE_CONTEXT.RESERVATION
		? getReservationOptionId(option)
		: getPropertyOptionId(option)
);

const buildGuideSelectionRoute = (type, guide, option) => {
	const pattern = getPreferredGuidePattern(guide, type);

	if (type === GUIDE_CONTEXT.RESERVATION) {
		const reservationId = getReservationOptionId(option);
		if (!reservationId) return null;
		if (pattern.includes("/report-a-problem")) {
			return `/reservations/${reservationId}/report-a-problem`;
		}
		return `/reservations/${reservationId}/details`;
	}

	const propertyId = getPropertyOptionId(option);
	if (!propertyId) return null;
	if (pattern.includes("/calendar/")) return `/calendar/${propertyId}`;
	if (pattern.includes("/guided-review")) return `/properties/${propertyId}/guided-review`;
	if (pattern.includes("/clone")) return `/properties/${propertyId}/clone`;
	return `/properties/${propertyId}/edit`;
};

const getPropertyTitle = (property) => property?.content?.title || property?.title || "Untitled Property";

const getPropertyAddress = (property) => {
	const address = property?.content?.location?.address || {};
	return [
		address.street_address,
		address.city,
		address.province,
		address.postal_code,
	].filter(Boolean).join(", ");
};

const getReservationGuestName = (reservation) => {
	const name = [reservation?.first_name, reservation?.last_name].filter(Boolean).join(" ");
	return name || reservation?.guest_name || reservation?.email || "Guest";
};

const getReservationDates = (reservation) => [
	reservation?.checkin_date,
	reservation?.checkout_date,
].filter(Boolean).join(" - ");

const isElementVisible = (element) => {
	if (!element || !element.isConnected) return false;
	const style = window.getComputedStyle(element);
	if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") return false;
	return element.getClientRects().length > 0;
};

const isElementDisabled = (element) => {
	if (!element) return false;
	if (element.disabled === true) return true;
	if (String(element.getAttribute("aria-disabled") || "").toLowerCase() === "true") return true;
	return Boolean(element.closest("fieldset[disabled], button[disabled], input[disabled], select[disabled], textarea[disabled], [aria-disabled='true']"));
};

const stepNeedsEnabledTarget = (step) => ["click", "focus", "input"].includes(step?.action);

const isElementUsableForStep = (element, step) => (
	!stepNeedsEnabledTarget(step) || !isElementDisabled(element)
);

const getActiveModal = () => {
	const modals = Array.from(document.querySelectorAll(".app-modal-overlay, .modal.show"));
	return modals.filter(isElementVisible).pop() || null;
};

const getVisibleStepCandidates = (selector) => {
	const candidates = Array.from(document.querySelectorAll(selector)).filter(isElementVisible);
	const activeModal = getActiveModal();
	const scopedCandidates = activeModal
		? candidates.filter((element) => activeModal.contains(element))
		: candidates;
	return Array.from(new Set(scopedCandidates));
};

const normalizeText = (value) => (
	value || ""
).replace(/([a-z])([A-Z])/g, "$1 $2")
	.replace(/[_-]+/g, " ")
	.replace(/[^a-zA-Z0-9]+/g, " ")
	.replace(/\s+/g, " ")
	.trim()
	.toLowerCase();

const GUIDE_TARGET_STOP_WORDS = new Set([
	"a", "an", "and", "as", "at", "button", "click", "field", "for", "from",
	"in", "into", "it", "modal", "new", "of", "on", "open", "or", "select",
	"step", "tab", "the", "this", "to", "type", "with", "you", "your",
]);

const textTokens = (value) => normalizeText(value)
	.split(" ")
	.filter((token) => token.length > 1 && !GUIDE_TARGET_STOP_WORDS.has(token));

const cssEscape = (value) => {
	if (typeof window !== "undefined" && window.CSS && typeof window.CSS.escape === "function") return window.CSS.escape(value);
	return String(value).replace(/[^a-zA-Z0-9_-]/g, "\\$&");
};

const selectorHintText = (selector) => {
	const values = [];
	for (const match of String(selector || "").matchAll(/[#.]([a-zA-Z0-9_-]+)/g)) {
		values.push(match[1]);
	}
	for (const match of String(selector || "").matchAll(/\[([^=\]]+)=['\"]?([^'\"\]]+)['\"]?\]/g)) {
		values.push(match[1], match[2]);
	}
	return values.join(" ");
};

const stepHintPhrases = (step) => {
	const phrases = [];
	if (step.selector_text_match) phrases.push(step.selector_text_match);
	phrases.push(selectorHintText(step.selector));
	const instruction = step.instruction || "";
	for (const match of instruction.matchAll(/["']([^"']{2,80})["']/g)) {
		phrases.push(match[1]);
	}
	phrases.push(instruction);
	return phrases.map(normalizeText).filter(Boolean);
};

const labelTextForElement = (element, root) => {
	const labels = [];
	if (element.id) {
		const linkedLabel = root.querySelector(`label[for="${cssEscape(element.id)}"]`);
		if (linkedLabel) labels.push(linkedLabel.textContent || "");
	}
	const wrappingLabel = element.closest("label");
	if (wrappingLabel) labels.push(wrappingLabel.textContent || "");
	const fieldWrap = element.closest(".fe-field-wrap, .form-group, .form-field, .field-wrap");
	if (fieldWrap) {
		const localLabels = Array.from(fieldWrap.querySelectorAll("label, .fe-label"));
		labels.push(...localLabels.map((label) => label.textContent || ""));
	}
	return labels.join(" ");
};

const descriptorTextForElement = (element, root) => [
	element.textContent || "",
	element.getAttribute("aria-label") || "",
	element.getAttribute("title") || "",
	element.getAttribute("placeholder") || "",
	element.getAttribute("name") || "",
	element.getAttribute("id") || "",
	element.getAttribute("value") || "",
	labelTextForElement(element, root),
].join(" ");

const scoreFallbackCandidate = (element, root, step) => {
	const descriptor = normalizeText(descriptorTextForElement(element, root));
	if (!descriptor) return 0;
	const phrases = stepHintPhrases(step);
	const tokens = Array.from(new Set(phrases.flatMap(textTokens)));
	let score = 0;
	for (const phrase of phrases) {
		if (!phrase) continue;
		if (descriptor === phrase) score += 100;
		else if (descriptor.includes(phrase)) score += 70;
	}
	for (const token of tokens) {
		if (descriptor.includes(token)) score += 12;
	}
	if (tokens.length === 1 && descriptor.split(" ").includes(tokens[0])) score += 30;
	if (/^(input|select|textarea|button|a)$/i.test(element.tagName)) score += 5;
	return score;
};

const findFallbackTarget = (step) => {
	const root = getActiveModal() || document;
	const candidates = Array.from(root.querySelectorAll([
		"input:not([type='hidden'])",
		"select",
		"textarea",
		"button",
		"a",
		"label",
		"[role='button']",
		"[tabindex]",
	].join(","))).filter(isElementVisible);

	let best = null;
	let bestScore = 0;
	for (const candidate of candidates) {
		const target = candidate.tagName.toLowerCase() === "label" && candidate.htmlFor
			? root.querySelector(`#${cssEscape(candidate.htmlFor)}`) || candidate
			: candidate;
		if (!isElementUsableForStep(target, step)) continue;
		const score = scoreFallbackCandidate(target, root, step);
		if (score > bestScore) {
			best = target;
			bestScore = score;
		}
	}

	return bestScore >= 24 ? best : null;
};

function UserGuideController({ location, navigate }) {
	const orgName = useSelector((state) => state.roleManager.org);
	const permissions = useSelector((state) => state.authState.permissions || []);

	const [activeGuide, setActiveGuide] = useState(null);
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const [targetDisplay, setTargetDisplay] = useState(null);
	const [targetUnavailable, setTargetUnavailable] = useState(false);
	const [waitNonce, setWaitNonce] = useState(0);
	const [drag, setDrag] = useState({ x: 0, y: 0, isDragging: false, startX: 0, startY: 0 });
	const [showSidebar, setShowSidebar] = useState(false);
	const [guideSelection, setGuideSelection] = useState(INITIAL_GUIDE_SELECTION);

	const observerRef = useRef(null);
	const popoverRef = useRef(null);
	const mutationTimeoutRef = useRef(null);
	const scrollTimeoutRef = useRef(null);

	// Stable refs so MutationObserver/setTimeout callbacks always see the latest values
	const activeGuideRef = useRef(activeGuide);
	const currentStepIndexRef = useRef(currentStepIndex);
	const targetDisplayRef = useRef(targetDisplay);
	const dragRef = useRef(drag);
	const locationRef = useRef(location);

	activeGuideRef.current = activeGuide;
	currentStepIndexRef.current = currentStepIndex;
	targetDisplayRef.current = targetDisplay;
	dragRef.current = drag;
	locationRef.current = location;

	const isAccountManagementPage =
		location.pathname === "/account-management" ||
		location.pathname.startsWith("/account-management/");
	const shouldShowHostAgentButton = !isAccountManagementPage;
	const hasHostBotAccess = permissions.includes("access_host_bot");
	const shouldShowHostSupportBtn = shouldShowHostAgentButton && hasHostBotAccess;

	const scrollToElement = useCallback((element) => {
		if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
		scrollTimeoutRef.current = setTimeout(() => {
			if (!element || !element.isConnected) return;
			if (element.scrollIntoView) {
				element.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
			}
		}, 400);
	}, []);

	const findTarget = useCallback(() => {
		const guide = activeGuideRef.current;
		const stepIndex = currentStepIndexRef.current;

		if (!guide) {
			setTargetDisplay(null);
			return;
		}

		const step = guide.steps[stepIndex];
		if (!step) {
			console.log("UserGuide: No more steps. Stopping guide.");
			setActiveGuide(null);
			setCurrentStepIndex(0);
			setTargetDisplay(null);
			setDrag({ x: 0, y: 0, isDragging: false, startX: 0, startY: 0 });
			localStorage.removeItem("user_guide_state");
			return;
		}

		let element = null;
		if (step.selector) {
			try {
				if (step.selector_text_match) {
					const candidatesArray = getVisibleStepCandidates(step.selector)
						.filter((candidate) => isElementUsableForStep(candidate, step));
					console.log(
						`UserGuide: Looking for '${step.selector}' containing '${step.selector_text_match}'... Visible candidates: ${candidatesArray.length}`,
					);

					const normalize = (s) => (s || "").replace(/\s+/g, " ").trim().toLowerCase();
					const looseCore = (s) => normalize(s).replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, "");
					const needle = normalize(step.selector_text_match);
					const needleCore = looseCore(step.selector_text_match);

					element = candidatesArray.find((el) => normalize(el.textContent) === needle);
					if (!element) {
						element = candidatesArray.find((el) =>
							normalize(el.textContent).includes(needle),
						);
					}
					if (!element && needleCore) {
						element = candidatesArray.find((el) =>
							looseCore(el.textContent).includes(needleCore),
						);
					}
					if (!element) {
						console.log(
							`UserGuide: No candidate matched '${step.selector_text_match}'. Candidate texts:`,
							candidatesArray.map((el) => (el.textContent || "").trim().slice(0, 60)),
						);
					}
				} else {
					element = getVisibleStepCandidates(step.selector)
						.filter((candidate) => isElementUsableForStep(candidate, step))[0] || null;
					console.log(`UserGuide: Looking for '${step.selector}'... Found?`, !!element);
				}
			} catch (e) {
				console.error(`UserGuide: Invalid selector provided '${step.selector}':`, e);
				element = null;
			}
		}

		if (!element) {
			element = findFallbackTarget(step);
			console.log("UserGuide: Fallback target resolution found?", !!element);
		}

		if (!element) {
			setTargetDisplay(null);
			return;
		}

		if (element !== targetDisplayRef.current) {
			console.log("UserGuide: Setting new target:", element, "Visible:", element.offsetParent !== null);
			setTargetDisplay(element);
			scrollToElement(element);
		}
	}, [scrollToElement]);

	// Mount: restore persisted state, start DOM watcher
	useEffect(() => {
		try {
			const saved = localStorage.getItem("user_guide_state");
			console.log("UserGuide: Attempting to restore state...", saved);
			if (saved) {
				const { guide, stepIndex } = JSON.parse(saved);
				if (guide && Array.isArray(guide.steps)) {
					console.log(
						"UserGuide: Restored guide",
						guide.title,
						"at step",
						stepIndex,
					);
					setActiveGuide(guide);
					setCurrentStepIndex(stepIndex);
				}
			}
		} catch (e) {
			console.error("Failed to restore guide state", e);
		}

		observerRef.current = new MutationObserver(() => {
			if (mutationTimeoutRef.current) clearTimeout(mutationTimeoutRef.current);
			mutationTimeoutRef.current = setTimeout(() => {
				findTarget();
			}, 300);
		});
		observerRef.current.observe(document.body, { childList: true, subtree: true, attributes: true });

		return () => {
			if (observerRef.current) observerRef.current.disconnect();
			if (mutationTimeoutRef.current) clearTimeout(mutationTimeoutRef.current);
			if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Location change: re-evaluate target
	useEffect(() => {
		console.log("UserGuide: Location changed to", location.pathname);
		findTarget();
	}, [location.pathname, findTarget]);

	useEffect(() => {
		if (!shouldShowHostSupportBtn && showSidebar) {
			setShowSidebar(false);
		}
	}, [shouldShowHostSupportBtn, showSidebar]);

	// Active guide / step change: persist state, re-find target, reset drag offset
	useEffect(() => {
		if (activeGuide) {
			localStorage.setItem(
				"user_guide_state",
				JSON.stringify({ guide: activeGuide, stepIndex: currentStepIndex }),
			);
			setTargetUnavailable(false);
			findTarget();
			setDrag({ x: 0, y: 0, isDragging: false, startX: 0, startY: 0 });
		}
	}, [activeGuide, currentStepIndex, findTarget]);

	// Poll for the current step's target, but only until a deadline. If the
	// element never appears (wrong tab/page, or unavailable for this item) we
	// stop polling and flip into a graceful "unavailable" state rather than
	// retrying forever. Optional steps auto-advance after a shorter grace, since
	// their target may legitimately not exist for this property/feature state.
	useEffect(() => {
		if (!activeGuide || targetDisplay) return undefined;
		const step = activeGuide.steps[currentStepIndex];
		const isOptional = Boolean(step && step.optional);
		const timeoutDuration = isOptional
			? GUIDE_OPTIONAL_STEP_WAIT_MS
			: GUIDE_TARGET_WAIT_MS;
		// Poll for the target until the deadline timeout stops us.
		const retryId = window.setInterval(() => {
			findTarget();
		}, 500);
		// Deadline must also clear the polling interval; otherwise findTarget
		// keeps firing forever in the "unavailable" path (that state change does
		// not re-run this effect, so the cleanup return would never trigger).
		const deadlineId = window.setTimeout(() => {
			window.clearInterval(retryId);
			if (isOptional) {
				console.log(
					"UserGuide: Optional step target absent; auto-advancing past step",
					currentStepIndexRef.current,
				);
				setCurrentStepIndex((index) => index + 1);
			} else {
				console.log(
					"UserGuide: Gave up waiting for the target of step",
					currentStepIndexRef.current,
				);
				setTargetUnavailable(true);
			}
		}, timeoutDuration);
		return () => {
			window.clearInterval(retryId);
			window.clearTimeout(deadlineId);
		};
	}, [activeGuide, currentStepIndex, targetDisplay, findTarget, waitNonce]);

	// Mouse drag: attach/detach global listeners
	const handleMouseMove = useCallback((e) => {
		if (!dragRef.current.isDragging) return;
		const newX = e.clientX - dragRef.current.startX;
		const newY = e.clientY - dragRef.current.startY;
		setDrag((prev) => ({ ...prev, x: newX, y: newY }));
	}, []);

	const handleMouseUp = useCallback(() => {
		if (dragRef.current.isDragging) {
			setDrag((prev) => ({ ...prev, isDragging: false }));
		}
	}, []);

	useEffect(() => {
		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [handleMouseMove, handleMouseUp]);

	const activateGuide = useCallback((guide) => {
		setActiveGuide(guide);
		setCurrentStepIndex(0);
		setTargetDisplay(null);
		setTargetUnavailable(false);
		setDrag({ x: 0, y: 0, isDragging: false, startX: 0, startY: 0 });
	}, []);

	// Restart the bounded search for the current step's target (used by the
	// "Try again" affordance after we gave up waiting).
	const retryTarget = useCallback(() => {
		setTargetUnavailable(false);
		setWaitNonce((nonce) => nonce + 1);
		findTarget();
	}, [findTarget]);

	// Advance past a step whose target we couldn't locate, without trying to
	// auto-click anything (there is nothing to click).
	const skipStep = useCallback(() => {
		setTargetUnavailable(false);
		setTargetDisplay(null);
		setCurrentStepIndex((index) => index + 1);
	}, []);

	const closeGuideSelection = useCallback(() => {
		setGuideSelection(INITIAL_GUIDE_SELECTION);
	}, []);

	const openGuideSelection = useCallback((type, guide) => {
		setGuideSelection({
			...INITIAL_GUIDE_SELECTION,
			show: true,
			type,
			guide,
		});
	}, []);

	const loadGuideSelectionOptions = useCallback(async (type, query = "") => {
		if (!orgName || !type) return;

		const queryValue = String(query || "");
		const trimmedQuery = queryValue.trim();
		const tid = getSessionId();

		setGuideSelection((prev) => ({
			...prev,
			isLoading: true,
			errorMessage: "",
		}));

		try {
			let response;
			const searchParam = trimmedQuery
				? `&keyword=${encodeURIComponent(trimmedQuery)}`
				: "";

			if (type === GUIDE_CONTEXT.PROPERTY) {
				response = await apiHandler.get({
					apiName: Constants.RAPAPI.NAME,
					path:
						`/properties?limit=${GUIDE_SELECTION_LIMIT}` +
						`&offset=0&tid=${encodeURIComponent(tid)}` +
						`&sort=${encodeURIComponent("property_id")}${encodeURIComponent("-")}` +
						"&fl=property_id,status,level,content,repconfig,organization_name,external_property_id" +
						`&organization=${encodeURIComponent(orgName)}&include_child_orgs=true${searchParam}`,
					options: { isCognito: true },
				});
			} else {
				const statusParam = trimmedQuery ? "" : "&status=current";
				response = await apiHandler.get({
					apiName: Constants.RAPAPI.NAME,
					path:
						`${Constants.RAPAPI.PROPERTY_RESERVATIONS}` +
						`?organization=${encodeURIComponent(orgName)}&include_child_orgs=true` +
						`&limit=${GUIDE_RESERVATION_SELECTION_LIMIT}&offset=0&tid=${encodeURIComponent(tid)}` +
						`&sort=${encodeURIComponent("book_date")}${encodeURIComponent("-")}` +
						`${statusParam}${searchParam}`,
					options: { isCognito: true },
				});
			}

			const options = Array.isArray(response?.data) ? response.data : [];

			setGuideSelection((prev) => {
				if (!prev.show || prev.type !== type || prev.query !== queryValue) return prev;
				const selectedId = getGuideSelectionOptionId(type, prev.selected);
				const selected = selectedId && options.some((option) => (
					getGuideSelectionOptionId(type, option) === selectedId
				)) ? prev.selected : null;
				return {
					...prev,
					options,
					selected,
					isLoading: false,
					errorMessage: "",
				};
			});
		} catch (err) {
			setGuideSelection((prev) => {
				if (!prev.show || prev.type !== type || prev.query !== queryValue) return prev;
				return {
					...prev,
					options: [],
					selected: null,
					isLoading: false,
					errorMessage: err?.message || "Unable to load options.",
				};
			});
		}
	}, [orgName]);

	useEffect(() => {
		if (!guideSelection.show || !guideSelection.type) return undefined;
		const delay = guideSelection.query.trim() ? 300 : 0;
		const timeoutId = window.setTimeout(() => {
			loadGuideSelectionOptions(guideSelection.type, guideSelection.query);
		}, delay);
		return () => window.clearTimeout(timeoutId);
	}, [
		guideSelection.show,
		guideSelection.type,
		guideSelection.query,
		loadGuideSelectionOptions,
	]);

	const confirmGuideSelection = useCallback(() => {
		const route = buildGuideSelectionRoute(
			guideSelection.type,
			guideSelection.guide,
			guideSelection.selected,
		);

		if (!route || !guideSelection.guide) return;

		const guide = guideSelection.guide;
		closeGuideSelection();
		navigate(route);
		activateGuide(guide);
	}, [guideSelection, closeGuideSelection, navigate, activateGuide]);

	const handleGuideSelectionSearchSubmit = useCallback((e) => {
		e.preventDefault();
		loadGuideSelectionOptions(guideSelection.type, guideSelection.query);
	}, [guideSelection.type, guideSelection.query, loadGuideSelectionOptions]);

	const deriveStartUrl = (guide) => {
		const patterns = getGuideUrlPatterns(guide);
		for (const pattern of patterns) {
			let url = pattern.replace(/^\^/, "").replace(/\$$/, "");
			url = url.replace(/\(.*?\)\??/g, "");
			if (/[\\()\[\]{}+*?|]/.test(url)) continue;
			if (url.length > 1 && url.endsWith("/")) continue;
			if (url.startsWith("/") && url.length > 1) return url;
		}
		return null;
	};

	const startGuide = (guide) => {
		const patterns = getGuideUrlPatterns(guide);
		const isOnCorrectPage = patterns.some((p) => {
			try {
				return new RegExp(p).test(location.pathname);
			} catch {
				return false;
			}
		});

		let activated = guide;

		if (!isOnCorrectPage) {
			const navLink = findMatchingNavLink(patterns);
			// Prefer a concrete URL we can navigate to directly (e.g. a
			// materialized "/properties/324203/edit/devices") before falling back
			// to prompting the host to select a property/reservation. The selection
			// prompt is only appropriate when the target id genuinely can't be
			// resolved (regex-only patterns and no current context).
			const startUrl = navLink ? null : deriveStartUrl(guide);
			if (navLink) {
				console.log(
					"UserGuide: Prepending navigation step to",
					navLink.path,
					"so the user lands on the right page before the guide starts.",
				);
				activated = {
					...guide,
					steps: [
						buildNavPrefixStep(navLink),
						...(guide.steps || []).map((s) => ({
							...s,
							order: (s.order || 1) + 1,
						})),
					],
				};
			} else if (startUrl) {
				console.log("UserGuide: Navigating to", startUrl, "before starting guide");
				navigate(startUrl);
			} else {
				const routeContext = getGuideRouteContext(guide);
				if (routeContext) {
					const currentId =
						routeContext === GUIDE_CONTEXT.PROPERTY
							? resolveCurrentPropertyId(location.pathname)
							: null;
					if (currentId) {
						const directRoute = buildGuideSelectionRoute(routeContext, guide, {
							property_id: currentId,
						});
						if (directRoute) {
							navigate(directRoute);
							activateGuide(guide);
							return;
						}
					}
					openGuideSelection(routeContext, guide);
					return;
				}
				console.log(
					"UserGuide: Cannot derive start URL or matching nav link — guide is armed, waiting for user to navigate",
				);
			}
		}

		activateGuide(activated);
	};

	const stopGuide = useCallback(() => {
		setActiveGuide(null);
		setCurrentStepIndex(0);
		setTargetDisplay(null);
		setDrag({ x: 0, y: 0, isDragging: false, startX: 0, startY: 0 });
		localStorage.removeItem("user_guide_state");
	}, []);

	const handleAutoAction = () => {
		if (!activeGuide) return;
		const currentStep = activeGuide.steps[currentStepIndex];

		if (targetDisplay && currentStep && !isElementUsableForStep(targetDisplay, currentStep)) {
			console.log("UserGuide: Target is disabled; waiting before advancing.");
			findTarget();
			return;
		}

		const nextStepIndex = currentStepIndex + 1;

		// Save BEFORE executing the action in case navigation causes an unload
		localStorage.setItem(
			"user_guide_state",
			JSON.stringify({ guide: activeGuide, stepIndex: nextStepIndex }),
		);

		if (targetDisplay && currentStep) {
			try {
				if (currentStep.action === "click") {
					const isExpanded = targetDisplay.getAttribute("aria-expanded");
					const isSelected = targetDisplay.getAttribute("aria-selected");
					const isChecked = targetDisplay.checked;

					if (isExpanded === "true" || isSelected === "true" || isChecked === true) {
						console.log("UserGuide: Element is already open/selected/checked, skipping click to prevent reversing state.");
					} else {
						targetDisplay.click();
						if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
						scrollTimeoutRef.current = setTimeout(() => {
							const nextTarget = targetDisplayRef.current;
							if (nextTarget && nextTarget.isConnected && nextTarget.scrollIntoView) {
								nextTarget.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
							}
						}, 450);
					}
				} else if (currentStep.action === "focus") {
					targetDisplay.focus();
				} else if (currentStep.action === "input") {
					targetDisplay.focus();
				}
			} catch (e) {
				console.error("Auto-action failed", e);
			}
		}

		setCurrentStepIndex(nextStepIndex);
	};

	const handleMouseDown = (e) => {
		setDrag((prev) => ({
			...prev,
			isDragging: true,
			startX: e.clientX - prev.x,
			startY: e.clientY - prev.y,
		}));
		e.preventDefault();
	};

	const renderGuideBubbleBody = (isWaitingForTarget = false) => (
		<Fragment>
			<Popover.Header
				as="h3"
				className="user-guide-drag-handle"
				onMouseDown={handleMouseDown}>
				<div className="d-flex justify-content-between align-items-center">
					<span>
						{activeGuide.title} ({currentStepIndex + 1}/
						{activeGuide.steps.length})
					</span>
					<button
						type="button"
						className="close user-guide-close-btn"
						onClick={stopGuide}>
						&times;
					</button>
				</div>
			</Popover.Header>
			<Popover.Body>
				{isWaitingForTarget && targetUnavailable ? (
					<Fragment>
						<p>
							We couldn't find the element for this step on the current
							page. It may live on a different tab or page, or it may not
							be available for this item.
						</p>
						<div className="d-flex justify-content-end gap-2">
							<button
								className="btn btn-sm btn-outline-secondary"
								onClick={retryTarget}>
								Try again
							</button>
							{currentStepIndex < activeGuide.steps.length - 1 && (
								<button
									className="btn btn-sm btn-primary"
									onClick={skipStep}>
									Skip step
								</button>
							)}
							<button
								className="btn btn-sm btn-outline-danger"
								onClick={stopGuide}>
								Stop
							</button>
						</div>
					</Fragment>
				) : (
					<Fragment>
						<p>{currentStep.instruction}</p>
						<div className="d-flex justify-content-end gap-2">
							<button
								className="btn btn-sm btn-primary"
								onClick={isWaitingForTarget ? findTarget : handleAutoAction}
								disabled={isWaitingForTarget}>
								{isWaitingForTarget
									? "Waiting..."
									: currentStepIndex === activeGuide.steps.length - 1
										? "Finish"
										: currentStep.action === "click"
											? "Click & Next"
											: "Next"}
							</button>
						</div>
					</Fragment>
				)}
			</Popover.Body>
		</Fragment>
	);

	const renderConnector = () => {
		const popover = popoverRef.current;
		if ((drag.x === 0 && drag.y === 0) || !targetDisplay || !popover) return null;

		const targetRect = targetDisplay.getBoundingClientRect();
		const popoverRect = popover.getBoundingClientRect();
		const x1 = targetRect.left + targetRect.width / 2;
		const y1 = targetRect.top + targetRect.height / 2;
		const x2 = popoverRect.left + popoverRect.width / 2;
		const y2 = popoverRect.top + popoverRect.height / 2;

		return (
			<div className="user-guide-connector-layer">
				<svg width="100%" height="100%">
					<line
						x1={x1}
						y1={y1}
						x2={x2}
						y2={y2}
						stroke="#0dcaf0"
						strokeWidth="3"
						strokeDasharray="5,5"
					/>
					<circle cx={x1} cy={y1} r="5" fill="#0dcaf0" />
				</svg>
			</div>
		);
	};

	const renderPropertyOption = (property) => {
		const propertyId = getPropertyOptionId(property);
		const isSelected = getGuideSelectionOptionId(guideSelection.type, guideSelection.selected) === propertyId;
		const address = getPropertyAddress(property);

		return (
			<button
				key={propertyId}
				type="button"
				className={`user-guide-selection-row${isSelected ? " selected" : ""}`}
				onClick={() => setGuideSelection((prev) => ({ ...prev, selected: property }))}>
				<span className="user-guide-selection-row-main">
					<span className="user-guide-selection-row-title">{getPropertyTitle(property)}</span>
					<span className="user-guide-selection-row-meta">
						#{propertyId}
						{property?.external_property_id ? ` · ${property.external_property_id}` : ""}
					</span>
				</span>
				{address && <span className="user-guide-selection-row-detail">{address}</span>}
			</button>
		);
	};

	const renderReservationOption = (reservation) => {
		const reservationId = getReservationOptionId(reservation);
		const isSelected = getGuideSelectionOptionId(guideSelection.type, guideSelection.selected) === reservationId;
		const dates = getReservationDates(reservation);

		return (
			<button
				key={reservationId}
				type="button"
				className={`user-guide-selection-row${isSelected ? " selected" : ""}`}
				onClick={() => setGuideSelection((prev) => ({ ...prev, selected: reservation }))}>
				<span className="user-guide-selection-row-main">
					<span className="user-guide-selection-row-title">{getReservationGuestName(reservation)}</span>
					<span className="user-guide-selection-row-meta">
						#{reservationId}
						{reservation?.property_id ? ` · Property #${reservation.property_id}` : ""}
					</span>
				</span>
				<span className="user-guide-selection-row-detail">
					{reservation?.property_title || "Untitled Property"}{dates ? ` · ${dates}` : ""}
				</span>
			</button>
		);
	};

	const isSelectingPropertyGuide = guideSelection.type === GUIDE_CONTEXT.PROPERTY;
	const guideSelectionTitle = isSelectingPropertyGuide ? "Select Property" : "Select Reservation";
	const guideSelectionPlaceholder = isSelectingPropertyGuide
		? "Search by property ID, reference ID, or title"
		: "Search by reservation ID, guest, or property";
	const guideSelectionEmptyText = isSelectingPropertyGuide
		? "No matching properties."
		: "No matching reservations.";
	const guideSelectionModal = (
		<AppModal
			show={guideSelection.show}
			onHide={closeGuideSelection}
			title={guideSelectionTitle}
			maxWidth={640}
			className="user-guide-selection-modal"
			body={(
				<div className="user-guide-selection">
					<form className="user-guide-selection-search" onSubmit={handleGuideSelectionSearchSubmit}>
						<input
							type="search"
							className="form-field"
							placeholder={guideSelectionPlaceholder}
							value={guideSelection.query}
							onChange={(e) => setGuideSelection((prev) => ({
								...prev,
								query: e.target.value,
								selected: null,
							}))}
						/>
						<button type="submit" className="primary-btn" disabled={guideSelection.isLoading}>
							Search
						</button>
					</form>

					<div className="user-guide-selection-results">
						{guideSelection.isLoading && (
							<div className="user-guide-selection-status">Loading...</div>
						)}
						{!guideSelection.isLoading && guideSelection.errorMessage && (
							<div className="user-guide-selection-error">{guideSelection.errorMessage}</div>
						)}
						{!guideSelection.isLoading && !guideSelection.errorMessage && guideSelection.options.length === 0 && (
							<div className="user-guide-selection-status">{guideSelectionEmptyText}</div>
						)}
						{!guideSelection.isLoading && !guideSelection.errorMessage && guideSelection.options.map((option) => (
							isSelectingPropertyGuide
								? renderPropertyOption(option)
								: renderReservationOption(option)
						))}
					</div>
				</div>
			)}
			footer={(
				<Fragment>
					<button type="button" className="primary-btn white-btn" onClick={closeGuideSelection}>
						Cancel
					</button>
					<button
						type="button"
						className="primary-btn"
						onClick={confirmGuideSelection}
						disabled={!guideSelection.selected}>
						Continue
					</button>
				</Fragment>
			)}
		/>
	);

	// --- Render ---
	if (!activeGuide && !shouldShowHostAgentButton) {
		return null;
	}

	const currentStep = activeGuide ? activeGuide.steps[currentStepIndex] : null;
	const isWaitingForTarget = activeGuide && currentStep && !targetDisplay;
	const waitingBubble = isWaitingForTarget
		? ReactDOM.createPortal(
			<div
				id="user-guide-popover"
				ref={popoverRef}
				className="popover show user-guide-bubble user-guide-bubble--waiting"
				style={{
					transform: `translate3d(${drag.x}px, ${drag.y}px, 0)`,
				}}>
				{renderGuideBubbleBody(true)}
			</div>,
			document.body,
		)
		: null;

	return (
		<Fragment>
			{guideSelectionModal}

			{shouldShowHostSupportBtn && (
				<Fragment>
					<button
						id="user-guide-help-btn"
						type="button"
						className="pull-right left-margin"
						onClick={() => setShowSidebar((prev) => !prev)}>
						<QuestionMarkIcon size={22} />
					</button>

					{showSidebar && (
						<div className="user-guide-sidebar">
							<div className="user-guide-sidebar-header">
								<span className="user-guide-sidebar-title">Host Support</span>
								<button
									type="button"
									className="user-guide-sidebar-close"
									onClick={() => setShowSidebar(false)}>
									&times;
								</button>
							</div>

							<div className="user-guide-sidebar-content">
								<HostAgentChat
									embedded={true}
									onStartGuide={(guide) => {
										startGuide(guide);
										setShowSidebar(false);
									}}
								/>
								{activeGuide && (
									<div className="user-guide-sidebar-body">
										<hr className="user-guide-sidebar-divider" />
										<button
											type="button"
											className="user-guide-sidebar-stop-btn"
											onClick={() => {
												stopGuide();
												setShowSidebar(false);
											}}>
											Stop Current Guide
										</button>
									</div>
								)}
							</div>
						</div>
					)}
				</Fragment>
			)}

			{activeGuide && currentStep && targetDisplay && (
				<Overlay
					show={true}
					target={targetDisplay}
					placement={currentStep.tooltip_position || "auto"}
					container={document.body}
					flip={true}
					popperConfig={{
						modifiers: [
							{
								name: "preventOverflow",
								options: {
									padding: 15,
									altAxis: true,
								},
							},
							{
								name: "flip",
								options: {
									padding: 15,
								},
							},
						],
					}}>
					{({
						placement,
						arrowProps,
						show,
						popper,
						hasDoneInitialMeasure,
						...props
					}) => {
						const {
							ref: overlayRef,
							style: overlayStyle,
							...restProps
						} = props;

						const mergedRef = (node) => {
							popoverRef.current = node;
							if (typeof overlayRef === "function") overlayRef(node);
							else if (overlayRef && "current" in overlayRef)
								overlayRef.current = node;
						};

						return (
							<Popover
								id="user-guide-popover"
								{...restProps}
								placement={placement}
								arrowProps={arrowProps}
								show={show}
								popper={popper}
								hasDoneInitialMeasure={hasDoneInitialMeasure}
								ref={mergedRef}
								className={(restProps.className || "") + " user-guide-bubble" + (drag.x !== 0 || drag.y !== 0 ? " user-guide-bubble--dragging" : "")}
								style={{
									...overlayStyle,
									transform: `${(overlayStyle && overlayStyle.transform) || ""} translate3d(${drag.x}px, ${drag.y}px, 0)`,
									zIndex: 50000,
									visibility: "visible",
									opacity: 1,
								}}>
								{renderGuideBubbleBody()}
							</Popover>
						);
					}}
				</Overlay>
			)}
			{waitingBubble}
			{/* Connector Line Rendered Outside Overlay to handle Z-Indexes and positioning */}
			{renderConnector()}
		</Fragment>
	);
}

export default withRouterHooks(UserGuideController);
